'use client';

import * as RadixDialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cx } from 'class-variance-authority';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Overlay from '../overlay';
import ScrollArea from '../scroll-area';
import Header, { HEADER_HEIGHT } from './header';
export type BottomSheetProps = {
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  title?: string;
  closeAriaLabel?: string;
  containerId?: string;
  overlayClassName?: string;
  portalId?: string;
};
const BottomSheet = ({
  open = false,
  onClose,
  children,
  title,
  closeAriaLabel,
  containerId,
  overlayClassName,
  portalId = 'footprint-footer'
}: BottomSheetProps) => {
  const {
    t
  } = useTranslation('ui');
  const portalRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const PADDING_TOP = 40;
  const containerHeight = containerRef.current?.clientHeight;
  const componentMaxHeight = containerHeight ? `${containerHeight - PADDING_TOP}px` : undefined;
  const scrollAreaMaxHeight = containerHeight ? `${containerHeight - HEADER_HEIGHT - PADDING_TOP}px` : undefined;
  useEffect(() => {
    if (portalId) {
      portalRef.current = document.getElementById(portalId);
    }
  }, [portalId]);
  useEffect(() => {
    if (containerId) {
      containerRef.current = document.getElementById(containerId);
    }
  }, [containerId]);
  useEffect(() => {
    if (open === true) {
      document.body.style.pointerEvents = 'auto';
    }
  }, [open]);
  return <RadixDialog.Root open={open} onOpenChange={onClose}>
      <RadixDialog.Portal container={portalRef.current || undefined}>
        <RadixDialog.Content onPointerDownOutside={onClose} aria-describedby={undefined} asChild>
          <div className={cx('fixed left-0 bottom-0 w-full bg-primary rounded-t-2xl z-[50] self-end', 'md:absolute', 'data-[state=open]:animate-bottomSheetIn', 'data-[state=closed]:animate-bottomSheetOut')}>
            <RadixDialog.Title asChild>
              <VisuallyHidden>{title}</VisuallyHidden>
            </RadixDialog.Title>
            <div className="flex flex-col" style={{
            maxHeight: componentMaxHeight
          }}>
              <Header closeAriaLabel={closeAriaLabel ?? t($ => $.components['bottom-sheet']['close-aria-label-default'])} onClose={onClose} title={title} />
              <ScrollArea hideBottomLine padding={5} maxHeight={scrollAreaMaxHeight}>
                {children}
              </ScrollArea>
            </div>
          </div>
        </RadixDialog.Content>
        <RadixDialog.Overlay asChild>
          <Overlay isVisible={open} className={overlayClassName} />
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>;
};
export default BottomSheet;